<template>
  <p-container>
    <p-row align-center>
      <p-button tertiary @click="back">
        <p-icon>keyboard_backspace</p-icon>
      </p-button>
      <h3 v-if="!$media.isMobile">
        {{ userDetail.email }}
      </h3>
    </p-row>
    <p-row>
      <p-col xs12 sm6 lg4>
        <p-card>
          <p-row column>
            <p-col>
              <p-text-field v-model="userDetail.userName" :disabled="!canEdit" :label="$t('core.page.user.list.username')"></p-text-field>
              <p-text-field v-model="userDetail.email" :disabled="!canEdit" type="email" :label="$t('core.page.registration.label.userEmail')"></p-text-field>
              <p-select
                v-model="userDetail.appHostRoleId"
                :label="$t('core.page.user.list.role')"
                :items="roles"
                :disabled="!canEdit"
              ></p-select>
              <p-select
                v-model="userDetail.statusId"
                :label="$t('core.page.user.action.status')"
                :items="verified"
                :disabled="!canEdit"
              ></p-select>
              <p-row column dense class="caption-1 my-3">
                <span v-if="userDetail.id">{{ $t('core.page.user.list.id') }}: {{ userDetail.id }}</span>
                <span v-if="userDetail.created">
                  {{ $t('core.page.user.page.created') }}: {{ $format.localDateAndTime(userDetail.created) }} | {{ $t('core.page.user.page.lastUpdated') }}: {{ $format.localDateAndTime(userDetail.modified) }}, {{ $t('core.page.user.page.from') }} {{ modifierName }} ({{ userDetail.modifierId }})
                </span>
              </p-row>
              <p-row column>
                <div><p-request-alert v-model="errorMessage" dismissable></p-request-alert></div>
                <div>
                  <p-alert v-model="successMessage" class="mt-4" type="success" dismissable>
                    {{ successMessage }}
                  </p-alert>
                </div>
              </p-row>
              <p-row>
                <p-button :loading="isLoading" :disabled="!canSave" @click="saveUser">
                  {{ $t('core.page.user.action.save') }}
                </p-button>
                <a :href="`mailto:${userDetail.email}`">
                  <p-button :loading="isLoading" :disabled="!userDetail.email || isLoading || !canEdit">
                    {{ $t('core.page.user.action.sendMail') }}
                  </p-button>
                </a>
              </p-row>
            </p-col>
          </p-row>
        </p-card>
      </p-col>
      <p-col xs12 sm6 lg4>
        <p-card :class="{ 'mb-4': $media.isDesktop, 'mb-2': $media.isTablet, 'mb-1': $media.isMobile}">
          <p-row column>
            <p-col>
              <h5 class="mb-2">
                {{ $t('core.page.user.list.verified') }}
              </h5>
              <div>{{ $t('core.page.user.page.code') }}: {{ userDetail.verificationCode }}</div>
              <div>{{ $t('core.page.user.page.codeSentOn') }}: {{ verificationSentOn }}</div>
              <div>{{ $t('core.page.user.page.codeValid') }}: {{ verificationExpires }}</div>
            </p-col>
            <p-col>
              <p-button :loading="isLoading" :disabled="isLoading || !canEdit" @click="sendCode">
                {{ $t('core.page.user.action.sendVerificationCode') }}
              </p-button>
            </p-col>
          </p-row>
        </p-card>
        <p-card class="mt-0">
          <p-row column>
            <p-col>
              <h5 class="mb-2">
                {{ $t('core.page.apiKey.page.title') }}
              </h5>
              <div>{{ $t('core.page.apiKey.page.description') }}</div>
            </p-col>
            <p-col>
              <p-button @click="goToAPIKeyList()">
                {{ $t('core.page.apiKey.button.toAPIKeyList') }}
              </p-button>
            </p-col>
          </p-row>
        </p-card>
      </p-col>
      <p-col v-if="isAdmin" xs12 sm6 lg4>
        <p-card>
          <p-row column>
            <p-col>
              <h5 class="mb-2">
                {{ $t("core.page.profile.label.resetPassword") }}
              </h5>
              <p-text-field v-model="setPassword.newPassword" type="password" :label="$t('core.page.profile.label.newPassword')"></p-text-field>
              <p-text-field v-model="setPassword.repeatNewPassword" type="password" :label="$t('core.page.profile.label.newPasswordRepeat')"></p-text-field>
            </p-col>
            <p-col>
              <p-button :loading="isLoading" :disabled="isLoading || !canEdit" @click="doSetPassword()">
                {{ $t('core.page.profile.button.doResetPassword') }}
              </p-button>
            </p-col>
          </p-row>
          <p-row column>
            <div><p-request-alert v-model="setPasswordErrorMessage" dismissable></p-request-alert></div>
            <div>
              <p-alert v-model="setPasswordSuccessMessage" type="success" dismissable>
                {{ setPasswordSuccessMessage }}
              </p-alert>
            </div>
          </p-row>
        </p-card>
      </p-col>
    </p-row>
  </p-container>
</template>

<script lang="ts">
  import Vue from 'vue';
  import UserResourceViewModel from '../../../services/v1/viewModel/resource/UserResourceViewModel';
  import ResendVerificationCodeRequestViewModel from '../../../services/v1/viewModel/request/Me/ResendVerificationCodeRequestViewModel';
  import SetPasswordForUserRequestModel from '../../../services/v2/model/set-password-for-user-request-model';

  export default Vue.extend({
    auth: true,
    role: ['admin', 'hostadmin'],
    layout: 'layout-hostadmin',
    data: () => ({
      setPassword: new SetPasswordForUserRequestModel(),
      isLoading: false,
      userDetail: new UserResourceViewModel(),
      sendVerification: new ResendVerificationCodeRequestViewModel(),
      verificationSentOn: undefined as string | undefined,
      verificationExpires: undefined as string | undefined,
      modifierName: undefined as string | undefined,
      errorMessage: undefined as string | undefined,
      successMessage: undefined as string | undefined,
      setPasswordSuccessMessage: undefined as string | undefined,
      setPasswordErrorMessage: undefined as string | undefined,
    }),
    computed: {
      canEdit(): boolean {
        if (!this.$auth.isInRole('HostAdmin') && this.userDetail.appHostRoleId === 1) {
          // Only a HostAdmin can edit a HostAdmin
          return false;
        }
        return true;
      },
      canSave(): boolean {
        return this.userDetail.userName !== undefined
          && this.userDetail.email !== undefined
          && this.userDetail.statusId !== undefined
          && this.userDetail.appHostRoleId !== undefined
          && !this.isLoading
          && this.canEdit;
      },
      roles(): {caption: string, id?: number, detail?: string}[] {
        // TODO: Get this from backend! At the very least the ids should not be hardcoded!
        let hostAdminEntry = [] as any[];
        if (this.$auth.isInRole('HostAdmin')) {
          hostAdminEntry = [{
            caption: this.$t('core.page.user.appHostRoleId.1'),
            id: 1,
          }];
        }
        return [
          ...hostAdminEntry,
          {
            caption: this.$t('core.page.user.appHostRoleId.2'),
            id: 2,
          },
          {
            caption: this.$t('core.page.user.appHostRoleId.3'),
            id: 3,
          },
          {
            caption: this.$t('core.page.user.appHostRoleId.4'),
            id: 4,
          },
        ];
      },
      verified(): {caption: string, id?: number}[] {
        // TODO: Get this from backend! At the very least the ids should not be hardcoded!
        return [
          {
            caption: this.$t('core.page.user.select.unverified'),
            id: 0,
          },
          {
            caption: this.$t('core.page.user.select.verified'),
            id: 1,
          },
          {
            caption: this.$t('core.page.user.select.blocked'),
            id: 2,
          },
          {
            caption: this.$t('core.page.user.select.disabled'),
            id: 3,
          },
        ];
      },
      userId(): number {
        const { id } = Vue.$router.currentRoute.params;
        return Number.parseInt(id, 10);
      },
      isAdmin(): boolean {
        let isAdmin = false;
        if (this.$auth.isLoggedIn) {
          isAdmin = this.$auth.isInRole('Admin', 'HostAdmin');
        }
        return isAdmin;
      },
    },
    async mounted() {
      await this.getUserInformation();
    },
    methods: {
      goToAPIKeyList() {
        this.$router.push(`/_admin/api-keys?userId=${this.userId}`);
      },
      async getUserInformation() {
        this.isLoading = true;
        try {
          await this.$auth.refresh();
          const response = await this.$service.api.core.users.getUserById(this.userId);
          this.userDetail = response;
          if (!this.userDetail.modifierId) {
            this.modifierName = 'None';
          } else {
            const modifier = await this.$service.api.core.users.getUserById(this.userDetail.modifierId);
            this.modifierName = modifier.userName!;
          }
          this.sendVerification.email = response.email!;
          this.verificationExpires = this.$format.localDateAndTime(this.userDetail.verificationCodeValidUntil as string);
          this.verificationSentOn = this.$format.localDateAndTime(this.userDetail.verificationCodeSentOn as string);
        } catch (error: any) {
          this.errorMessage = error;
        }
        this.isLoading = false;
      },
      async sendCode() {
        if (!this.canEdit) {
          return;
        }
        this.isLoading = true;
        try {
          await this.$service.api.core.me.resendVerificationCode(this.sendVerification);
          this.verificationSentOn = this.$format.localDateAndTime(this.$date.now().format());
          this.successMessage = this.$t('core.page.user.message.verficationCodeWasSendSuccessfuly');
          await this.getUserInformation();
        } catch (error: any) {
          this.errorMessage = error;
        }
        this.isLoading = false;
      },
      async saveUser() {
        if (!this.canEdit) {
          return;
        }
        if (!this.userDetail.id) {
          this.errorMessage = this.$t('core.page.user.message.noUserIdAvaible');
          return;
        }

        this.isLoading = true;
        try {
          const allPromises = [];
          allPromises.push(this.$service.api.core.users.updateUser(this.userDetail.id, this.userDetail));
          allPromises.push(this.$service.api.core.users.setUserStatus(this.userDetail.id, this.userDetail.statusId!));
          allPromises.push(this.$service.api.core.users.setUserAppRole(this.userDetail.id, this.userDetail.appHostRoleId!));

          await Promise.all(allPromises);
          await this.getUserInformation();
          this.successMessage = this.$t('core.page.user.message.userWasSuccessfulyUpdated');
        } catch (error: any) {
          this.errorMessage = error;
        }
        this.isLoading = false;
      },
      back() {
        this.$router.back();
      },
      async doSetPassword() {
        this.isLoading = true;
        try {
          const confirmed = await this.$dialog.confirm(this.$t('core.page.user.page.confirmPasswordChange'), `${this.$t('core.page.user.message.confirmChangePassword')} (${this.userDetail.email})`);
          if (confirmed) {
            this.setPassword.id = this.userDetail.id;
            await this.$service.v2.api.core.users.setPasswordForUser(this.setPassword)
              .then(() => {
                this.setPassword.newPassword = '';
                this.setPassword.repeatNewPassword = '';
                this.setPasswordSuccessMessage = this.$t('core.page.user.message.passwordChangedSuccessfully');
              });
          }
        } catch (error : string | any) {
          this.setPasswordErrorMessage = error;
        }
        this.isLoading = false;
      },
    },
  });
</script>
